import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Button, Field, Icon, Modal, Picker, DialogCancel } from "@cntrl/ui";
import { UploadField } from "../../releases/UploadField.js";
import { useReleases } from "../../../app/hooks/useReleases.js";
import { useRelease } from "../../../app/hooks/useRelease.js";
import { useLinkInBio } from "../../../app/hooks/useLinkInBio.js";
import { TargetingField, RADIUS_DEFAULT, type TargetingToken } from "./TargetingField.js";
import { InterestSuggestions } from "./InterestSuggestions.js";
import { LookalikeBlock, type LookalikeSource } from "./LookalikeBlock.js";
import {
  customPlacements as buildCustomPlacements,
  smartPlacements,
  smartPlatformOf,
  type SmartPlatform,
} from "./adPlacements.js";
import {
  useCreativeStatus,
  useInterestSuggestions,
  useLookalikeSources,
  useLookalikeStatus,
  useSaveDraft,
  useUploadAdCreative,
  useCreateCampaign,
  type AdForm,
} from "../../../app/hooks/useAdCreator.js";
import { errorMessage } from "../../../app/hooks/apiError.js";
import { openSettingsTab } from "../../../app/settingsPanel.js";
import { pushToast } from "../../../app/toasts.js";
import { Segmented } from "./Segmented.js";
import { CharCounter } from "./CharCounter.js";
import styles from "./AdCreatorModal.module.scss";

// The Meta Ad Creator (shots 45–46, /marketing/campaigns/new) — the in-app campaign builder:
// promote picker → creative dropzone (+ live IG-Reel preview) → copy → audience → placements → budget → create-paused
// → Push Live. Resumable via ad_drafts (auto-saved). The video creative chunk-uploads to Meta in core-worker; the
// modal polls the draft until the creative is ready. "Push Live" is Idem-keyed and provider-guarded (a missing grant
// surfaces the status strip, never a dead button).

// The reference's four, in its order and with its labels. `OUTCOME_LEADS` — not SALES: a band's conversion is
// a sign-up (mailing list, pre-save), and a "Sales" objective points Meta at a purchase event no music campaign
// fires, so it optimises against a signal that never arrives.
const OBJECTIVES: { value: string; label: string }[] = [
  { value: "OUTCOME_TRAFFIC", label: "Traffic (link clicks)" },
  { value: "OUTCOME_AWARENESS", label: "Reach / awareness" },
  { value: "OUTCOME_ENGAGEMENT", label: "Engagement" },
  { value: "OUTCOME_LEADS", label: "Leads (sign-ups)" },
];
// `LISTEN_MUSIC` is the reference's default and Meta's music-specific call to action; `LISTEN_NOW` renders the
// same two words on the ad but is the generic one.
const CTAS: { value: string; label: string }[] = [
  { value: "LISTEN_MUSIC", label: "Listen now" },
  { value: "LEARN_MORE", label: "Learn more" },
  { value: "WATCH_MORE", label: "Watch more" },
  { value: "SHOP_NOW", label: "Shop now" },
  { value: "GET_TICKETS", label: "Get tickets" },
  { value: "SIGN_UP", label: "Sign up" },
];
const HEADLINE_MAX = 40;
const TEXT_MAX = 125;
const NAME_MAX = 80;

const SMART_CARDS: { value: SmartPlatform; title: string; sub: string }[] = [
  { value: "both", title: "Both", sub: "Instagram + Facebook · widest reach" },
  { value: "instagram", title: "Instagram only", sub: "Younger / visual-first audiences" },
  { value: "facebook", title: "Facebook only", sub: "Older / shareable copy + link" },
];

const PLACEMENTS_HINT =
  "Runs on Feed, Reels, and Stories on the selected platform(s). Skips Audience Network, Marketplace, and " +
  "Messenger inbox, those convert poorly for music.";

/** The Custom grid, with the reference's own defaults. The unchecked ones are present rather than omitted
 *  because "we deliberately do not run here" is a different statement from "this placement does not exist". */
const CUSTOM_PLACEMENTS: { platform: "instagram" | "facebook"; position: string; label: string; on: boolean }[] = [
  { platform: "instagram", position: "stream", label: "IG Feed", on: true },
  { platform: "instagram", position: "reels", label: "IG Reels", on: true },
  { platform: "instagram", position: "story", label: "IG Stories", on: true },
  { platform: "instagram", position: "explore", label: "IG Explore", on: false },
  { platform: "facebook", position: "feed", label: "FB Feed", on: true },
  { platform: "facebook", position: "facebook_reels", label: "FB Reels", on: true },
  { platform: "facebook", position: "story", label: "FB Stories", on: true },
  { platform: "facebook", position: "marketplace", label: "FB Marketplace", on: false },
  { platform: "facebook", position: "video_feeds", label: "FB In-stream video", on: false },
];

/** The band's IG handle for the preview — its name, slugified, exactly as the reference builds it. */
function slugifyHandle(name: string | null | undefined): string {
  const slug = (name ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "").trim();
  return slug || "yourband";
}

function defaultForm(releaseId?: string, destinationUrl?: string): AdForm {
  return {
    promoteType: releaseId ? "release" : "url",
    destinationUrl: destinationUrl ?? "",
    releaseId: releaseId ?? null,
    campaignName: "",
    objective: "OUTCOME_TRAFFIC",
    headline: "",
    primaryText: "",
    cta: "LISTEN_MUSIC",
    countries: "United States",
    ageMin: 18,
    ageMax: 44,
    regions: "",
    cities: "",
    radiusMiles: RADIUS_DEFAULT,
    placementMode: "smart",
    platforms: ["facebook", "instagram"],
    budgetMode: "duration",
    dailyBudgetCents: 500,
    durationDays: 7,
    createPaused: true, // the P6-1 safety default is ON
  };
}

export interface AdCreatorModalProps {
  onClose: () => void;
  onPushed: () => void;
  initialReleaseId?: string;
  initialDestinationUrl?: string;
}

export function AdCreatorModal({ onClose, onPushed, initialReleaseId, initialDestinationUrl }: AdCreatorModalProps) {
  const [form, setForm] = useState<AdForm>(() => defaultForm(initialReleaseId, initialDestinationUrl));
  // The creative SESSION row this upload binds to. It is a ref because `persist` and `onPushLive` read it
  // synchronously, and a mirrored state because the readiness poll's `enabled` has to re-render on it — a ref
  // alone would leave the poll switched off until some unrelated render happened to follow.
  const draftIdRef = useRef<string | null>(null);
  const [sessionDraftId, setSessionDraftId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [creativeStatus, setCreativeStatus] = useState<"none" | "uploading" | "ready" | "failed">("none");
  const [creativeReason, setCreativeReason] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  // Resolved targeting, held as KEY+NAME pairs: the key is what Meta takes, the name is what the chip reads.
  // Countries are ISO-2 codes (searchable multi-select, ART-205); the default seat is the US.
  const [countryTokens, setCountryTokens] = useState<TargetingToken[]>([{ key: "US", name: "United States" }]);
  const [cityTokens, setCityTokens] = useState<TargetingToken[]>([]);
  const [regionTokens, setRegionTokens] = useState<TargetingToken[]>([]);
  const [interestTokens, setInterestTokens] = useState<TargetingToken[]>([]);
  // The lookalike audience. Off by default; on, it REPLACES interests / cities / states (countries + ages stay).
  const [lookalikeOn, setLookalikeOn] = useState(false);
  const [lookalikeSource, setLookalikeSource] = useState<LookalikeSource | null>(null);
  // Set only when the mirror is "An audience from my ad account".
  const [lookalikeAudienceId, setLookalikeAudienceId] = useState<string | null>(null);
  const [lookalikeRatio, setLookalikeRatio] = useState(1);
  // "Skip your existing fans" — ON by default (ART-295 §2): excludes everyone the mirror holds, engagers included.
  const [skipFollowers, setSkipFollowers] = useState(true);
  const [customPlacements, setCustomPlacements] = useState<string[]>(
    CUSTOM_PLACEMENTS.filter((p) => p.on).map((p) => `${p.platform}:${p.position}`),
  );

  const releases = useReleases({});
  // The chosen release's own composed read — the ONE place the public landing path is authoritative
  // (`smartLink.publicPath`). The reference slugified the band name client-side and hoped; resolving it from
  // the release means the ad's destination is the URL the band's own page actually serves.
  const chosenRelease = useRelease(form.promoteType === "release" ? (form.releaseId ?? undefined) : undefined);
  // The preview handle. The band's PUBLIC SLUG rather than a client-side slugify of its name: the slug is the
  // canonical form and it is what the ad's own landing page is served from.
  const linkInBio = useLinkInBio();
  const saveDraft = useSaveDraft();
  const { uploadCreative } = useUploadAdCreative();
  const createCampaign = useCreateCampaign();

  // Poll THIS creative's readiness, and only while a video is uploading to Meta (the worker stamps the row when
  // it finishes). It used to poll the band's whole drafts LIST for the same answer; that list was the
  // save-and-resume surface and is gone.
  const creativeQuery = useCreativeStatus(
    sessionDraftId,
    creativeStatus === "uploading" ? 2500 : undefined,
  );

  // Seeded from the band's artist profile, once per session — this spends a vendor call per profile seed, so
  // it is the modal being OPEN that pays for it, never a page that merely mounts the creator.
  const suggestions = useInterestSuggestions();
  const lookalikeSources = useLookalikeSources(lookalikeOn);
  const lookalikeData = lookalikeSources.data;
  const lookalikeReady = lookalikeData?.apiStatus?.state === "ok";
  // A pick only counts while the server still calls it eligible — a stale choice must not reach Push live.
  const pickedAudience = lookalikeData?.audiences?.find((a) => a.id === lookalikeAudienceId);
  const lookalikePick: LookalikeSource | null =
    lookalikeOn && lookalikeReady && lookalikeSource
      ? lookalikeSource === "audience"
        ? pickedAudience?.eligible
          ? "audience"
          : null
        : lookalikeData?.sources?.some((s) => s.source === lookalikeSource && s.eligible)
          ? lookalikeSource
          : null
      : null;
  // Pre-select the first eligible mirror the moment they arrive (IG + FB fans first), so turning the block on is
  // one click.
  useEffect(() => {
    if (!lookalikeOn || lookalikeSource) return;
    const first = lookalikeData?.sources?.find((s) => s.eligible)?.source;
    if (first) setLookalikeSource(first as LookalikeSource);
  }, [lookalikeOn, lookalikeSource, lookalikeData]);
  const lookalikeStatus = useLookalikeStatus(
    lookalikePick
      ? {
          source: lookalikePick,
          audienceId: lookalikePick === "audience" ? lookalikeAudienceId : null,
          ratioPercent: lookalikeRatio,
          countries: countryTokens.map((t) => t.key),
        }
      : null,
  );

  const patch = useCallback((p: Partial<AdForm>) => setForm((f) => ({ ...f, ...p })), []);

  // Open the creative session row the upload binds to (reusing this session's if it already exists).
  //
  // There is no debounced AUTO-SAVE beside this any more. It existed for save-and-resume — a feature with no
  // legacy counterpart, removed 2026-09-09 — so it was writing the whole form to a row nothing would ever read
  // back, once per 900ms of typing, for the entire time the dialog was open.
  const persist = useCallback(
    async (next: AdForm): Promise<string> => {
      const saved = await saveDraft.mutateAsync({
        draftId: draftIdRef.current,
        name: next.campaignName || "Untitled campaign",
        form: next,
      });
      draftIdRef.current = saved.draftId ?? draftIdRef.current;
      setSessionDraftId(draftIdRef.current);
      return draftIdRef.current!;
    },
    [saveDraft],
  );

  // While a video uploads, watch the server's own verdict (worker → ready/failed). `ready` is the server's word
  // and is stamped only WITH a Meta video id, so there is nothing further for the client to check — and nothing
  // for it to hold: the create reads the creative straight off the row.
  useEffect(() => {
    if (creativeStatus !== "uploading") return;
    const status = creativeQuery.data?.status;
    if (status === "ready") setCreativeStatus("ready");
    else if (status === "failed") {
      setCreativeStatus("failed");
      setCreativeReason(creativeQuery.data?.reason ?? "upload-failed");
    }
  }, [creativeQuery.data, creativeStatus]);

  async function onPickFile(picked: File | null) {
    setFile(picked);
    if (!picked) return;
    const kind: "image" | "video" = picked.type.startsWith("video/") ? "video" : "image";
    setCreativeStatus("uploading");
    setCreativeReason(null);
    setProgress(0);
    try {
      const draftId = await persist(form); // open the session row the creative binds to
      const result = await uploadCreative(picked, draftId, kind, (fr) => setProgress(fr), undefined);
      setProgress(undefined);
      const status = (result.status as "uploading" | "ready" | "failed") ?? "uploading";
      patch({ creative: { mediaId: result.mediaId ?? "", kind, status } });
      // An image is ready immediately; a video stays "uploading" until the worker (poll) reports ready.
      setCreativeStatus(status === "ready" ? "ready" : "uploading");
    } catch (err) {
      setProgress(undefined);
      setCreativeStatus("failed");
      setCreativeReason(errorMessage(err, "Upload failed"));
      pushToast(errorMessage(err, "Couldn't upload the creative"));
    }
  }

  async function onPushLive() {
    if (!canPush) return;
    setPushing(true);
    try {
      const draftId = draftIdRef.current ?? (await persist(form));
      const result = await createCampaign.mutateAsync({
        draftId,
        name: form.campaignName || "Untitled campaign",
        objective: form.objective ?? "OUTCOME_TRAFFIC",
        promoteType: form.promoteType ?? "url",
        destinationUrl: form.destinationUrl ?? "",
        releaseId: form.promoteType === "release" ? form.releaseId ?? null : null,
        headline: form.headline ?? "",
        primaryText: form.primaryText ?? "",
        callToAction: form.cta ?? "LEARN_MORE",
        targeting: {
          countries: countryTokens.map((t) => t.key),
          ageMin: form.ageMin ?? 18,
          ageMax: form.ageMax ?? 65,
          // KEYS, not the words the user typed — see TargetingField. A lookalike replaces all three, so they
          // are not sent while it is on (the server drops them too).
          regions: lookalikeOn ? [] : regionTokens.map((t) => t.key),
          cities: lookalikeOn
            ? []
            : cityTokens.map((t) => ({ key: t.key, radiusMiles: t.radiusMiles ?? form.radiusMiles ?? RADIUS_DEFAULT })),
          interestIds: lookalikeOn ? [] : interestTokens.map((t) => t.key),
        },
        lookalike: lookalikePick
          ? {
              source: lookalikePick,
              ratioPercent: lookalikeRatio,
              audienceId: lookalikePick === "audience" ? lookalikeAudienceId : null,
              skipFollowers,
            }
          : undefined,
        placements,
        dailyBudgetCents: form.dailyBudgetCents ?? 500,
        durationDays: form.budgetMode === "continuous" ? null : form.durationDays ?? 7,
        createPaused: form.createPaused ?? true,
      });
      if (result.apiStatus?.state !== "ok") {
        pushToast(statusMessage(result.apiStatus?.state));
        setPushing(false);
        return;
      }
      pushToast(form.createPaused ? "Campaign created (paused) — launch it from the campaigns table." : "Campaign is live on Meta.", "success");
      onPushed();
    } catch (err) {
      pushToast(errorMessage(err, "Couldn't push the campaign live"));
      setPushing(false);
    }
  }

  const handle = slugifyHandle(linkInBio.data?.slug);

  // The Smart-picks card selection, DERIVED from the platforms the form already holds rather than stored
  // beside them — two fields for one fact is how a saved draft comes back disagreeing with itself.
  const smartPlatform: SmartPlatform = smartPlatformOf(form.platforms);
  const smartPositions = useMemo(() => smartPlacements(smartPlatform), [smartPlatform]);
  const customPositions = useMemo(() => buildCustomPlacements(customPlacements), [customPlacements]);
  const placements = (form.placementMode ?? "smart") === "smart" ? smartPositions : customPositions;

  // The release's resolved landing page. Written into the form ONCE per release choice rather than every
  // render, so a user who edits the URL by hand keeps their edit.
  const resolvedReleaseUrl = useMemo(() => {
    const path = chosenRelease.data?.data?.smartLink?.publicPath;
    if (!path) return null;
    const base = typeof window !== "undefined" ? window.location.origin : "";
    return path.startsWith("http") ? path : `${base}${path.startsWith("/") ? "" : "/"}${path}`;
  }, [chosenRelease.data]);

  const seededReleaseRef = useRef<string | null>(null);
  useEffect(() => {
    if (form.promoteType !== "release" || !form.releaseId || !resolvedReleaseUrl) return;
    if (seededReleaseRef.current === form.releaseId) return;
    seededReleaseRef.current = form.releaseId;
    patch({ destinationUrl: resolvedReleaseUrl });
  }, [form.promoteType, form.releaseId, resolvedReleaseUrl, patch]);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const totalCents = (form.dailyBudgetCents ?? 0) * (form.budgetMode === "continuous" ? 1 : form.durationDays ?? 0);
  const canPush =
    creativeStatus === "ready" &&
    !!(form.destinationUrl ?? "").trim() &&
    // The reference requires a campaign name. Without it the create falls back to "Untitled campaign", which
    // is a real, spending campaign nobody can find again in the table.
    !!(form.campaignName ?? "").trim() &&
    (form.promoteType !== "release" || !!form.releaseId) &&
    // A zero-placement custom selection would reach Meta as AUTOMATIC placements — the opposite of what was
    // asked for — so it blocks the submit rather than being quietly filled in.
    placements.publisherPlatforms.length > 0 &&
    (form.dailyBudgetCents ?? 0) > 0 &&
    // A lookalike needs an eligible source and a country to build over.
    (!lookalikeOn || (!!lookalikePick && countryTokens.length > 0)) &&
    !pushing;

  return (
    <Modal
      dirty="auto"
      title="Build a new campaign"
      // `3xl` and a FLUSH body, the show editor's shape: this dialog's body is a two-pane workbench above a
      // pinned ribbon, not a stack of fields, so it needs the card's own height to size against. Under the
      // primitive's padded body the whole card grew instead — the preview scrolled away with the form and the
      // footer fell below the fold, which is what "isn't contained to viewport height at all" describes.
      size="3xl"
      bodyLayout="flush"
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <DialogCancel />
          <Button onClick={() => void onPushLive()} disabled={!canPush} loading={pushing}>
            {pushing ? "Building campaign…" : "Push live to Meta"}
          </Button>
        </div>
      }
    >
      <div className={styles.workbench}>
        {/* ---- Form: the ONLY scroll region in the card ---- */}
        <div className={styles.pane}>
          <section className={styles.block}>
            <h3 className={styles.legend}>What are you promoting?</h3>
            <Segmented
              ariaLabel="Promote type"
              value={form.promoteType ?? "url"}
              onChange={(v) => patch({ promoteType: v })}
              options={[
                { value: "url", label: "Paste URL" },
                { value: "release", label: "Use a release" },
              ]}
            />
            {form.promoteType === "release" && (
              <Picker
                label="Release"
                className={styles.label}
                value={form.releaseId ?? ""}
                onChange={(releaseId) => patch({ releaseId: releaseId || null })}
                options={[
                  { value: "", label: "Choose a release…" },
                  // "(no link)" is flagged IN THE OPTION, not discovered after choosing: a release with no
                  // smart-link slug has no landing page to send the click to, and the reference makes that
                  // visible at the moment of choosing rather than at submit.
                  ...(releases.data?.items ?? []).map((r) => ({
                    value: r.releaseId ?? "",
                    label: `${r.title ?? "Untitled"}${r.smartLinkSlug ? "" : " (no link)"}`,
                  })),
                ]}
              />
            )}
            <Field
              label="Destination URL"
              placeholder="Paste a Spotify, smart-link, tickets or merch URL"
              value={form.destinationUrl ?? ""}
              onChange={(e) => patch({ destinationUrl: e.target.value })}
              hint={resolvedReleaseUrl ? `Resolved from the release: ${resolvedReleaseUrl}` : undefined}
            />
            <div className={styles.label}>
              <div className={styles.labelRow}>
                <span className={styles.labelText}>Campaign name</span>
                <CharCounter count={(form.campaignName ?? "").length} max={NAME_MAX} />
              </div>
              <input
                className={styles.input}
                maxLength={NAME_MAX}
                placeholder="Release push · your release name"
                aria-label="Campaign name"
                value={form.campaignName ?? ""}
                onChange={(e) => patch({ campaignName: e.target.value })}
              />
            </div>
            <Picker
              label="Objective"
              className={styles.label}
              value={form.objective ?? ""}
              onChange={(objective) => patch({ objective })}
              options={OBJECTIVES.map((o) => ({ value: o.value, label: o.label }))}
            />
          </section>

          <section className={styles.block}>
            <h3 className={styles.legend}>Creative</h3>
            <UploadField
              accept="image/*,video/*"
              file={file}
              onFile={onPickFile}
              progress={progress}
              // The creator's Creative block is five fields deep and the dropzone is the shortest-lived of
              // them, so it takes the compact rung rather than the cover-art one this control defaults to.
              density="compact"
              prompt="Drop a photo or video — photos up to 100 MB, videos up to 500 MB"
            />
            <p className={styles.creativeStatus} data-state={creativeStatus}>
              {creativeStatus === "uploading" && "Uploading to Meta…"}
              {creativeStatus === "ready" && "Creative ready ✓"}
              {creativeStatus === "failed" && `Upload failed — ${creativeReason ?? "try again"}`}
              {creativeStatus === "none" && "Add a photo or video to preview and push."}
            </p>

            <div className={styles.label}>
              <div className={styles.labelRow}>
                <span className={styles.labelText}>Headline</span>
                <CharCounter count={(form.headline ?? "").length} max={HEADLINE_MAX} />
              </div>
              <input
                className={styles.input}
                maxLength={HEADLINE_MAX}
                placeholder="New single out now"
                value={form.headline ?? ""}
                onChange={(e) => patch({ headline: e.target.value })}
              />
            </div>

            <div className={styles.label}>
              <div className={styles.labelRow}>
                <span className={styles.labelText}>Primary text</span>
                <CharCounter count={(form.primaryText ?? "").length} max={TEXT_MAX} />
              </div>
              <textarea
                className={styles.textarea}
                maxLength={TEXT_MAX}
                placeholder="The thing you want fans to know in one line."
                value={form.primaryText ?? ""}
                onChange={(e) => patch({ primaryText: e.target.value })}
              />
            </div>

            <Picker
              label="Call to action"
              className={styles.label}
              value={form.cta ?? ""}
              onChange={(cta) => patch({ cta })}
              options={CTAS.map((c) => ({ value: c.value, label: c.label }))}
            />
          </section>

          <section className={styles.block}>
            <h3 className={styles.legend}>Audience</h3>
            {/* Lookalike FIRST: it decides whether the manual targeting below applies at all. */}
            <LookalikeBlock
              enabled={lookalikeOn}
              onEnabledChange={setLookalikeOn}
              sources={lookalikeData?.sources ?? []}
              audiences={lookalikeData?.audiences ?? []}
              loading={lookalikeOn && lookalikeSources.isLoading}
              // The request failing (a 403, a 500) is not a Meta verdict — say what actually happened.
              error={lookalikeSources.isError ? errorMessage(lookalikeSources.error, "Couldn't load your audiences.") : null}
              apiStatus={lookalikeData?.apiStatus?.state ?? null}
              apiCta={lookalikeData?.apiStatus?.cta ?? null}
              onRetry={() => void lookalikeSources.refetch()}
              onReconnect={() => openSettingsTab("connections")}
              source={lookalikeSource}
              audienceId={lookalikeAudienceId}
              onSourceChange={(src, audienceId) => {
                setLookalikeSource(src);
                if (src === "audience") setLookalikeAudienceId(audienceId ?? null);
              }}
              ratio={lookalikeRatio}
              onRatioChange={setLookalikeRatio}
              skipFollowers={skipFollowers}
              onSkipFollowersChange={setSkipFollowers}
              status={lookalikeStatus.data}
              statusLoading={lookalikeStatus.isLoading && lookalikeStatus.fetchStatus !== "idle"}
            />
            <div className={styles.row2}>
              <Field label="Age min" type="number" value={String(form.ageMin ?? 18)} onChange={(e) => patch({ ageMin: Number(e.target.value) || 18 })} />
              <Field label="Age max" type="number" value={String(form.ageMax ?? 44)} onChange={(e) => patch({ ageMax: Number(e.target.value) || 65 })} />
            </div>
            {/* Countries is a searchable MULTI-SELECT over the closed ISO country list — no vendor call, and
                more than one selectable, like States (ART-205, Ryan). */}
            <TargetingField
              label="Countries"
              kind="country"
              localOptions={COUNTRY_OPTIONS}
              tokens={countryTokens}
              onChange={setCountryTokens}
            />
            {!lookalikeOn && (
              <>
                {/* Cities, states and interests are RESOLVED, never typed: Meta's targeting spec takes opaque keys,
                    and a free-text box here sends it words it rejects at ad-set create — a form filled in correctly
                    that fails later and elsewhere. The States field shipped exactly that way. */}
                <TargetingField
                  label="Cities (optional)"
                  kind="city"
                  tokens={cityTokens}
                  onChange={setCityTokens}
                  withRadius
                  radiusMiles={form.radiusMiles ?? RADIUS_DEFAULT}
                  onRadiusChange={(radiusMiles) => {
                    patch({ radiusMiles });
                    setCityTokens((prev) => prev.map((t) => ({ ...t, radiusMiles })));
                  }}
                />
                <TargetingField label="States (optional)" kind="region" tokens={regionTokens} onChange={setRegionTokens} />
                <TargetingField
                  label="Interests (optional)"
                  kind="interest"
                  tokens={interestTokens}
                  onChange={setInterestTokens}
                />
                {/* The suggestions strip sits BELOW the interest typeahead (ART-205, Ryan). */}
                <InterestSuggestions
                  suggestions={suggestions.data?.options ?? []}
                  hasSeeds={suggestions.data?.hasSeeds ?? false}
                  loading={suggestions.isLoading}
                  apiStatus={suggestions.data?.apiStatus?.state}
                  tokens={interestTokens}
                  onChange={setInterestTokens}
                />
              </>
            )}
          </section>

          <section className={styles.block}>
            <h3 className={styles.legend}>Placements</h3>
            <Segmented
              ariaLabel="Placement mode"
              value={form.placementMode ?? "smart"}
              onChange={(v) => patch({ placementMode: v })}
              options={[
                { value: "smart", label: "Smart picks" },
                { value: "custom", label: "Custom" },
              ]}
            />

            {(form.placementMode ?? "smart") === "smart" ? (
              <>
                {/* Three CARDS, a radiogroup — not two independent toggles. The toggles could express
                    "neither", which Meta has no meaning for, and could not express the three-way choice the
                    copy beneath describes. */}
                <div className={styles.platformCards} role="radiogroup" aria-label="Platforms">
                  {SMART_CARDS.map((card) => (
                    <button
                      key={card.value}
                      type="button"
                      role="radio"
                      aria-checked={smartPlatform === card.value}
                      data-selected={smartPlatform === card.value || undefined}
                      className={styles.platformCard}
                      onClick={() =>
                        patch({
                          platforms:
                            card.value === "both" ? ["facebook", "instagram"] : [card.value],
                        })
                      }
                    >
                      <span className={styles.platformCardTitle}>{card.title}</span>
                      <span className={styles.platformCardSub}>{card.sub}</span>
                    </button>
                  ))}
                </div>
                <p className={styles.placementsHint}>{PLACEMENTS_HINT}</p>
                <ul className={styles.placementChips}>
                  {CUSTOM_PLACEMENTS.filter((p) => p.on && smartPositions.publisherPlatforms.includes(p.platform)).map((p) => (
                    <li key={`${p.platform}:${p.position}`} className={styles.placementChip} data-platform={p.platform}>
                      {p.label}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <div className={styles.placementGrid}>
                  {CUSTOM_PLACEMENTS.map((p) => {
                    const key = `${p.platform}:${p.position}`;
                    const on = customPlacements.includes(key);
                    return (
                      <label key={key} className={styles.placementCheck}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() =>
                            setCustomPlacements((prev) => (on ? prev.filter((x) => x !== key) : [...prev, key]))
                          }
                        />
                        <span>{p.label}</span>
                      </label>
                    );
                  })}
                </div>
                {customPlacements.length === 0 ? (
                  // Never a silent default: an empty custom selection means "run nowhere", which Meta would
                  // answer by running EVERYWHERE (automatic placements), so it has to be an error the user
                  // resolves rather than a gap we fill.
                  <p className={styles.placementError} role="alert">
                    Pick at least one placement, or switch to Smart picks
                  </p>
                ) : null}
              </>
            )}
          </section>

          <section className={styles.block}>
            <h3 className={styles.legend}>Budget</h3>
            <Segmented
              ariaLabel="Budget mode"
              value={form.budgetMode ?? "duration"}
              onChange={(v) => patch({ budgetMode: v })}
              options={[
                { value: "duration", label: "End after N days" },
                { value: "continuous", label: "Run continuously" },
              ]}
            />
            <div className={styles.row2}>
              <Field
                label="Daily budget ($)"
                type="number"
                value={String((form.dailyBudgetCents ?? 0) / 100)}
                onChange={(e) => patch({ dailyBudgetCents: Math.max(0, Math.round(Number(e.target.value) * 100)) })}
              />
              {form.budgetMode !== "continuous" && (
                <Field label="Duration (days)" type="number" value={String(form.durationDays ?? 7)} onChange={(e) => patch({ durationDays: Math.max(1, Number(e.target.value) || 1) })} />
              )}
            </div>
            <div className={styles.totalBanner}>
              {/* A continuous budget's real number is the MONTHLY one — "$5/day" is the figure people
                  under-read, and the reference spells out the ~30-day projection beside it. */}
              {form.budgetMode === "continuous"
                ? `${money(form.dailyBudgetCents ?? 0)}/day · runs continuously · roughly ${money((form.dailyBudgetCents ?? 0) * 30)}/month`
                : `Total: ${money(totalCents)} over ${form.durationDays ?? 0} days`}
            </div>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={form.createPaused ?? true} onChange={(e) => patch({ createPaused: e.target.checked })} />
              <span>Create paused, review before it goes live (no spend until you Launch it from the campaigns table)</span>
            </label>
          </section>
        </div>

        {/* ---- IG Reel preview: the STATIC panel, the show editor's rail mirrored to the trailing edge.
                It does not scroll with the form — the whole point of a live preview is that it is still there
                when you are typing the copy it previews, and it used to travel off the top of the card. ---- */}
        <aside className={styles.previewCol}>
          <p className={styles.previewLabel}>Ad preview</p>
          <div className={styles.phone}>
            <div className={styles.reel}>
              {previewUrl && file?.type.startsWith("video/") ? (
                <video className={styles.media} src={previewUrl} muted playsInline />
              ) : previewUrl ? (
                <img className={styles.media} src={previewUrl} alt="" />
              ) : (
                <span className={styles.reelEmpty}>
                  <Icon icon={Play} size={22} />
                  Upload an image or video to preview your ad here
                </span>
              )}
              <div className={styles.reelMeta}>
                <span className={styles.reelBand}>{handle} · Sponsored</span>
                <span className={styles.reelText}>{form.primaryText || "Your primary text shows here…"}</span>
                <span className={styles.reelCta}>{CTAS.find((c) => c.value === form.cta)?.label ?? "Learn more"} ›</span>
              </div>
            </div>
          </div>
          <div className={styles.headlinePreview}>
            <span className={styles.headlinePreviewLabel}>Headline</span>
            {form.headline || "Headline preview"}
          </div>
        </aside>
      </div>
    </Modal>
  );
}

// The closed ISO 3166-1 alpha-2 country set with English display names — the searchable Countries picker's
// options (ART-205). Meta targeting requires ISO-2, which is exactly the token key. Built from Intl so the
// list is complete rather than a hand-kept table; falls back to the major markets where Intl is unavailable.
const FALLBACK_ISO2 = [
  "US", "CA", "MX", "GB", "IE", "AU", "NZ", "DE", "FR", "ES", "IT", "NL", "BE", "LU", "AT", "CH", "SE", "NO", "DK",
  "FI", "IS", "PT", "PL", "CZ", "SK", "HU", "RO", "BG", "GR", "HR", "SI", "EE", "LV", "LT", "UA", "TR", "IL", "AE",
  "SA", "ZA", "NG", "KE", "EG", "MA", "BR", "AR", "CL", "CO", "PE", "UY", "JP", "KR", "CN", "HK", "TW", "IN", "ID",
  "PH", "TH", "VN", "MY", "SG",
];
const COUNTRY_OPTIONS: { key: string; name: string }[] = (() => {
  // NOTE: `Intl.supportedValuesOf` has NO "region" key — passing one throws RangeError — so the list is built
  // by walking every AA–ZZ code through `Intl.DisplayNames`, which returns the code itself for an unassigned
  // one (default `fallback: "code"`). That is complete, needs no hand-kept table, and cannot throw.
  const display =
    typeof Intl !== "undefined" && "DisplayNames" in Intl ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
  if (!display) return FALLBACK_ISO2.map((code) => ({ key: code, name: code }));
  // Supranational / special ISO codes DisplayNames resolves to a real name but Meta does not target as a
  // country — keep them out of the picker.
  const NON_COUNTRY = new Set(["EU", "EZ", "UN", "QO", "ZZ"]);
  const out: { key: string; name: string }[] = [];
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      if (NON_COUNTRY.has(code)) continue;
      let name: string | undefined;
      try { name = display.of(code); } catch { name = undefined; }
      if (name && name !== code) out.push({ key: code, name });
    }
  }
  return out.sort((x, y) => x.name.localeCompare(y.name));
})();

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusMessage(state?: string | null): string {
  switch (state) {
    case "not-connected":
      return "Connect Meta in Settings → Connections to run ads.";
    case "ads-permission-missing":
      return "Reconnect Meta and allow ad access to push campaigns.";
    case "no-ad-account":
      return "Pick the ad account for this band in Settings → Connections.";
    case "custom-audience-terms":
      return "Meta needs you to accept its Custom Audience terms once — open the lookalike block for the link.";
    default:
      return "Meta is temporarily unavailable — try again in a moment.";
  }
}
